//@flow
import * as Immutable from 'immutable';

import { ZoneMap } from 'common/models/flow/ZoneMap';
import type { ZoneMapModelType } from 'common/models/flow/ZoneMap';
import { TeamId } from './TeamId';
import { ZoneMapGeometry } from './ZoneMapGeometry';
import { ZoneMapFolder } from './ZoneMapFolder';
import { LatLng } from 'common/models/internal/LatLng';
import { Actor } from './Actor';

import { LocationId } from './LocationId';

import { AuditMetadata } from 'common/models/internal/AuditMetadata';

class InternalZoneMap extends ZoneMap {
    static fromJS(json: ZoneMapModelType): InternalZoneMap {
        const state: Object = Object.assign({}, json);
        state.customProperties = Immutable.Map(state.customProperties);
        state.teamId = TeamId.fromJS(state.teamId);
        state.homebaseId = LocationId.fromJS(state.homebaseId);
        state.geometries = Immutable.List(state.geometries).map(item =>
            ZoneMapGeometry.fromJS(item)
        );
        state.folders = Immutable.List(state.folders).map(item =>
            ZoneMapFolder.fromJS(item)
        );
        state.actor = Actor.fromJS(state.actor);
        state.defaultCenter = state.defaultCenter
            ? LatLng.fromJS(state.defaultCenter)
            : state.defaultCenter;
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        return new this(Immutable.Map(state));
    }

    updateGeometry({
        geometry
    }: {
        geometry: ZoneMapGeometry
    }): InternalZoneMap {
        const currentIndex = this.geometries.findIndex(
            filterableGeometry =>
                filterableGeometry.id.getId() === geometry.id.getId()
        );
        const updatedGeometries =
            currentIndex >= 0
                ? this.geometries.set(currentIndex, geometry)
                : this.geometries.push(geometry);
        return this.setGeometries(updatedGeometries);
    }

    updateFolder({ folder }: { folder: ZoneMapFolder }): InternalZoneMap {
        const currentIndex = this.folders.findIndex(
            filterableFolder => filterableFolder.id === folder.id
        );
        const updatedFolders =
            currentIndex >= 0
                ? this.folders.set(currentIndex, folder)
                : this.folders.push(folder);
        return this.setFolders(updatedFolders);
    }

    get teamId(): TeamId {
        return this._state.get('teamId');
    }

    get homebaseId(): LocationId {
        return this._state.get('homebaseId');
    }

    get geometries(): Immutable.List<ZoneMapGeometry> {
        return this._state.get('geometries');
    }

    get folders(): Immutable.List<ZoneMapFolder> {
        return this._state.get('folders');
    }

    get actor(): Actor {
        return this._state.get('actor');
    }

    get defaultCenter(): ?LatLng {
        return this._state.get('defaultCenter');
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }
}

export { InternalZoneMap as ZoneMap };
